import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Spinner from "../../Components/Spinner/Spinner";
import { MdDeleteSweep } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";
import {
  authorizedDel,
  authorizedGet,
  authorizedPost,
  authorizedPut,
} from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import MessageBar, {
  config,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../utils/helper";
import Strings from "../../utils/Strings";
import { useNavigate } from "react-router-dom";
import { MdDelete } from "react-icons/md";

const ToDo = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [saveNote, setSaveNote] = useState("");
  const [updateIndex, setUpdateIndex] = useState(0);
  const [isUpdateButtonVisible, setIsUpdateButtonVisible] = useState(false);
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [noteToDeleteIndex, setNoteToDeleteIndex] = useState(null);
  const [category, setCategory] = useState("general");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    fetchGetNotes();
  }, []);

  //Function for get note API call
  const fetchGetNotes = async () => {
    try {
      const res = await authorizedGet(
        `${configuration.apis.getTask}?adminId=${adminId}&userId=${userId}`,
        config,
      );
      const response = res?.data?.data;
      if (response) {
        setNotes(response);
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.log("Error fetching get Notes:", error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  //Function for add note API call
  const handleAddNotes = async () => {
    if (saveNote.trim() === "") {
      setNoteError("Please add some details");
      return;
    }
    try {
      const data = {
        userId: userId,
        adminId: adminId,
        notes: saveNote,
        category: category,
      };

      setUpdateLoading(true);
      const res = await authorizedPost(
        configuration.apis.addTask,
        data,
        config,
      );
      const response = res?.data;
      if (res?.status) {
        successNotify(response?.message);
      } else {
        errorNotify(response?.message);
      }
      if (response?.data) {
        setNotes(response.data);
      }
    } catch (error) {
      console.log("Error adding note:", error);
      errorNotify(Strings.ERROR);
    } finally {
      setUpdateLoading(false);
      closeModal();
      setCategory("general");
    }
  };

  //Function for update API call
  const handleUpdateNotes = async () => {
    if (saveNote.trim() === "") {
      setNoteError("Please add some details");
      return;
    }
    try {
      setUpdateLoading(true);
      const data = {
        noteId: notes[updateIndex]._id,
        adminId: adminId,
        notes: saveNote,
      };

      const res = await authorizedPut(
        configuration.apis.updateTask,
        data,
        config,
      );
      const response = res?.data;
      if (res?.status) {
        successNotify(response?.message);
        setNotes(response?.data);
      } else {
        errorNotify(response.message);
      }
    } catch (error) {
      console.log("Error updating note:", error);
      errorNotify(Strings.ERROR);
    } finally {
      setUpdateLoading(false);
      setIsModalVisible(false);
      setIsUpdateButtonVisible(false);
      setSaveNote("");
      setCategory("general");
    }
  };

  //Function to open the modal
  const handleSubmit = () => {
    setIsModalVisible(true);
  };

  //Function to update the note data
  const handleNoteChange = (event) => {
    setSaveNote(event.target.value);
    if (noteError) setNoteError(""); // clear error on change
  };

  //Function to remove or delete the note
  const removeNote = async (index) => {
    try {
      setDeleting(true);
      const noteId = notes[index]._id;
      const res = await authorizedDel(
        `${configuration.apis.deleteTask}?adminId=${adminId}&noteId=${noteId}`,
        config,
      );
      const response = res?.data;
      if (response?.status) {
        successNotify(response?.message);
        const updatedNotes = notes.filter((_, i) => i !== index);
        setNotes(updatedNotes);
      } else {
        errorNotify(response?.message);
      }
    } catch (error) {
      console.log("Error deleting note:", error);
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  //Function to update the note
  const updatedNote = (index, category) => {
    setCategory(category);
    setIsModalVisible(true);
    setSaveNote(notes[index].notes);
    setIsUpdateButtonVisible(true);
    setUpdateIndex(index);
  };

  //Function to close the modal
  const closeModal = () => {
    setCategory("general");
    setIsModalVisible(false);
    setIsUpdateButtonVisible(false);
    setSaveNote("");
  };

  const confirmDeleteNote = async () => {
    if (noteToDeleteIndex !== null) {
      await removeNote(noteToDeleteIndex);
      setNoteToDeleteIndex(null); // clear after delete
    }
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <div>
        {loading ? (
          <div className="loader flex h-screen items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="ml-5 text-center text-2xl text-gray-800">
                Admin Tasks
              </h2>
              <button
                className="mr-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 md:px-6 md:text-base"
                onClick={handleSubmit}
              >
                + Add Task
              </button>
            </div>

            <div className="">
              {notes?.length !== 0 ? (
                <ul className="space-y-4">
                  {notes.map((list, index) => (
                    <li
                      key={index}
                      className="ml-5 mr-5 grid grid-cols-1 gap-3 rounded-lg border bg-white p-4 transition hover:shadow-md"
                    >
                      <div className="flex flex-cols items-start justify-between">
                        <div className="flex items-center gap-3">
                          <p className="text-base text-gray-700">
                            {list?.notes}
                          </p>
                        </div>
                        <div className="flex md-flex items-center gap-3 text-gray-500">
                          <button
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => updatedNote(index, list?.category)}
                          >
                            <RiEdit2Fill size={20} />
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700"
                            onClick={() => {
                              setNoteToDeleteIndex(index);
                              setShowDeleteConfirmation(true);
                            }}
                          >
                            <MdDelete size={20} />
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-xs text-green-600">
                        {notes[index]?.updatedAt ? (
                          <>
                            <span>
                              Created at{" "}
                              {new Date(
                                notes[index]?.createdAt,
                              ).toLocaleDateString("en-CA")}
                            </span>
                            <span>
                              Updated at{" "}
                              {new Date(
                                notes[index]?.updatedAt,
                              ).toLocaleDateString("en-CA")}
                            </span>
                          </>
                        ) : (
                          <span>
                            Created at{" "}
                            {new Date(
                              notes[index]?.createdAt,
                            ).toLocaleDateString("en-CA")}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center text-gray-500">
                  No tasks available
                </div>
              )}
            </div>
          </div>
        )}

        {isModalVisible && (
          <div className="popup-container fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-[80%] items-center justify-center rounded-lg bg-white p-8 shadow-lg md:w-[50%]">
              <div className="flex flex-col justify-center gap-3">
                <div className="flex w-full flex-row justify-between xs:items-center">
                  <h2 className="text-start text-base font-semibold xs:text-center xs:text-lg">
                    Add a Task
                  </h2>
                </div>
                <textarea
                  className="mb-4 w-full rounded border border-gray-300 p-2"
                  rows="4"
                  value={saveNote}
                  onChange={handleNoteChange}
                  placeholder="Enter your note here..."
                />
                {noteError && (
                  <p className="mb-2 text-sm text-red-500">{noteError}</p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    className="rounded bg-gray-300 px-1 py-0.5 text-black md:px-4 md:py-2"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                  {isUpdateButtonVisible ? (
                    <button
                      className="rounded bg-blue-500 px-1 py-0.5 text-white md:px-4 md:py-2"
                      onClick={handleUpdateNotes}
                      disabled={updateLoading}
                    >
                      {updateLoading ? <Spinner size="small" /> : "Update"}
                    </button>
                  ) : (
                    <button
                      className="rounded bg-blue-500 px-1 py-0.5 text-white md:px-4 md:py-2"
                      onClick={handleAddNotes}
                      disabled={updateLoading}
                    >
                      {updateLoading ? <Spinner size="small" /> : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-[90%] rounded-lg bg-white p-4 shadow-lg sm:w-[60%] md:w-[40%]">
              <p className="mb-4 text-center text-sm sm:text-base">
                {Strings.DELETE_NOTE}
              </p>
              <div className="flex justify-around">
                <button
                  onClick={confirmDeleteNote}
                  className="rounded bg-red-500 px-4 py-2 text-white"
                  disabled={deleting}
                >
                  {deleting ? <Spinner size="small" /> : "Yes, Delete"}
                </button>
                <button
                  onClick={() => {
                    setNoteToDeleteIndex(null);
                    setShowDeleteConfirmation(false);
                  }}
                  className="rounded bg-gray-300 px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <MessageBar />
    </>
  );
};

export default ToDo;
